import { View, ViewDensity } from "@common/types/views/views";
import { ViewConfig } from "@lib/views";
import {
    AccrualImporterSort,
    AccrualImporterViewRow,
} from "./accrualImporter.types";
import {
    AccrualImporterActionsCell,
    AccrualImporterCounterpartyCell,
    AccrualImporterDocumentDateCell,
    AccrualImporterDocumentNumberCell,
    AccrualImporterEntityCell,
    AccrualImporterNameCell,
    AccrualImporterRowNumberCell,
    AccrualImporterStatusCell,
    AccrualImporterTotalCell,
} from "./accrualDocumentImporterCells";
import { withExtractingSkeleton } from "./withExtractingSkeleton";

const RowNumberCell = withExtractingSkeleton(AccrualImporterRowNumberCell);
const NameCell = withExtractingSkeleton(AccrualImporterNameCell);
const EntityCell = withExtractingSkeleton(AccrualImporterEntityCell);
const CounterpartyCell = withExtractingSkeleton(
    AccrualImporterCounterpartyCell,
);
const DocumentNumberCell = withExtractingSkeleton(
    AccrualImporterDocumentNumberCell,
);
const DocumentDateCell = withExtractingSkeleton(
    AccrualImporterDocumentDateCell,
);
const TotalCell = withExtractingSkeleton(AccrualImporterTotalCell);

enum AccrualDocumentImporterColumn {
    ROW_NUMBER = "rowNumber",
    NAME = "name",
    STATUS = "status",
    ENTITY = "entity",
    COUNTERPARTY = "counterparty",
    NUMBER = "number",
    DOCUMENT_DATE = "documentDate",
    TOTAL = "total",
    ACTIONS = "actions",
}

export function getAccrualDocumentImporterViewConfig(
    counterpartyColumnHeader: string,
): ViewConfig<
    AccrualImporterViewRow,
    AccrualDocumentImporterColumn,
    AccrualImporterSort
> {
    return {
        columns: {
            [AccrualDocumentImporterColumn.ROW_NUMBER]: {
                accessorKey: "rowNumber",
                name: "#",
                header: "#",
                cell: RowNumberCell,
                minSize: 40,
                maxSize: 48,
                immutable: true,
                hiddenFromSettings: true,
            },
            [AccrualDocumentImporterColumn.NAME]: {
                accessorKey: "id",
                name: "File name",
                header: "File name",
                cell: NameCell,
                minSize: 260,
                maxSize: 600,
                sort: {
                    asc: AccrualImporterSort.FILE_NAME_ASC,
                    desc: AccrualImporterSort.FILE_NAME_DESC,
                },
            },
            [AccrualDocumentImporterColumn.STATUS]: {
                accessorKey: "id",
                name: "Status",
                header: "Status",
                cell: AccrualImporterStatusCell,
                minSize: 180,
                maxSize: 240,
                sort: {
                    asc: AccrualImporterSort.STATUS_ASC,
                    desc: AccrualImporterSort.STATUS_DESC,
                },
            },
            [AccrualDocumentImporterColumn.ENTITY]: {
                accessorKey: "financialDocument",
                name: "Entity",
                header: "Entity",
                cell: EntityCell,
                minSize: 120,
                maxSize: 160,
            },
            [AccrualDocumentImporterColumn.COUNTERPARTY]: {
                accessorKey: "id",
                name: counterpartyColumnHeader,
                header: counterpartyColumnHeader,
                cell: CounterpartyCell,
                minSize: 220,
                maxSize: 320,
            },
            [AccrualDocumentImporterColumn.NUMBER]: {
                accessorKey: "id",
                name: "Number",
                header: "Number",
                cell: DocumentNumberCell,
                minSize: 120,
                maxSize: 160,
            },
            [AccrualDocumentImporterColumn.DOCUMENT_DATE]: {
                accessorKey: "id",
                name: "Date",
                header: "Date",
                cell: DocumentDateCell,
                minSize: 120,
                maxSize: 160,
            },
            [AccrualDocumentImporterColumn.TOTAL]: {
                accessorKey: "id",
                name: "Total",
                header: "Total",
                cell: TotalCell,
                align: "right",
                minSize: 120,
                maxSize: 160,
            },
            [AccrualDocumentImporterColumn.ACTIONS]: {
                accessorKey: "id",
                name: "Actions",
                header: "",
                cell: AccrualImporterActionsCell,
                minSize: 130,
                maxSize: 130,
                immutable: true,
                hiddenFromSettings: true,
            },
        },
    };
}

export function getAccrualDocumentImporterView(): View {
    return {
        filters: {},
        options: { density: ViewDensity.MD },
        columns: [
            { id: AccrualDocumentImporterColumn.ROW_NUMBER },
            { id: AccrualDocumentImporterColumn.NAME },
            { id: AccrualDocumentImporterColumn.STATUS },
            { id: AccrualDocumentImporterColumn.ENTITY },
            { id: AccrualDocumentImporterColumn.COUNTERPARTY },
            { id: AccrualDocumentImporterColumn.NUMBER },
            { id: AccrualDocumentImporterColumn.DOCUMENT_DATE },
            { id: AccrualDocumentImporterColumn.TOTAL },
            {
                id: AccrualDocumentImporterColumn.ACTIONS,
                options: { pin: "right" },
            },
        ],
    };
}
